import { useMemo } from 'react';
import dayjs from 'dayjs';
import { RaporCekmecesi } from '../rapor-ekrani/RaporCekmecesi';
import { RaporTablosu } from '../rapor-ekrani/RaporTablosu';
import { RaporHatasi } from '../rapor-ekrani/RaporHatasi';
import { useRaporSorgu } from '../rapor-ekrani/useRaporSorgu';
import { useDarEkran } from '../rapor-ekrani/useDarEkran';
import { paraMetni, tarihMetni } from '../rapor-ekrani/bicim';
import type { RaporKolonu } from '../rapor-ekrani/kolon';
import type { PanelEkranKaynagi } from '../../types/panelConfig';
import { Kutu } from './StokSuzgecleri';
import {
  CIKIS_TURU_ADI, birimMaliyet, fisSatirlari, lokasyonSatiri, stokDegeri,
  type CikisTuru, type Degerleme, type FisSatiri, type HamFisSatiri, type HamLokasyonSatiri, type LokasyonSatiri,
  type StokCikisSatiri, type StokDegerSatiri,
} from './tipler';

/** Stok Değer çekmecesindeki "Son çıkış hareketleri": bugünden geriye bu kadar gün, en çok bu kadar satır. */
const SON_CIKIS_GUN = 90;
const SON_CIKIS_SATIR = 10;

/**
 * Çıkış satırlarının kolonları. `tutarli`: Stok Çıkış çekmecesi (Tutar kolonu + toplam satırı);
 * Stok Değer çekmecesinde yalnız miktar yazar, toplam yoktur.
 */
function fisKolonlari(tutarli: boolean): RaporKolonu<FisSatiri>[] {
  const k: RaporKolonu<FisSatiri>[] = [
    { id: 'tarih', baslik: 'Tarih', tur: 'tarih', deger: (f) => f.tarih, kart: 'baslik' },
    { id: 'fis_turu', baslik: 'Fiş Türü', tur: 'metin', deger: (f) => f.fisTuru, esnek: true, kart: 'baslik' },
    { id: 'fis_no', baslik: 'Fiş No', tur: 'kod', deger: (f) => f.fisNo, kart: 'alt' },
    { id: 'lokasyon', baslik: 'Lokasyon', tur: 'metin', deger: (f) => f.lokasyon, kart: 'alt' },
    { id: 'miktar', baslik: 'Miktar', tur: 'sayi', deger: (f) => f.miktar, toplam: tutarli || undefined, kart: tutarli ? 'olcuAlt' : 'olcu' },
  ];
  if (tutarli) k.push({ id: 'tutar', baslik: 'Tutar', tur: 'para', deger: (f) => f.tutar, toplam: true, kart: 'olcu' });
  return k;
}

const GOVDE = (telefon: boolean) =>
  telefon ? 'flex-1 flex flex-col gap-2 px-4 pt-3 rk-kenar-16' : 'flex-1 min-h-0 flex flex-col gap-3 p-4';

// ── Stok Değer ──────────────────────────────────────────────────────────────────

interface DegerProps {
  /** Açık ürün; `undefined` ise çekmece kapalıdır. */
  stok: StokDegerSatiri | undefined;
  /** Listede seçili değerleme: lokasyon değerleri bununla hesaplanır. */
  degerleme: Degerleme;
  lokasyonKaynagi: PanelEkranKaynagi | undefined;
  fislerKaynagi: PanelEkranKaynagi | undefined;
  /** 'YYYY-MM-DD': son çıkış hareketlerinin bitiş günü. */
  bugun: string;
  onKapat: () => void;
}

/**
 * S345: ürünün maliyet kutuları, lokasyonlara dağılımı ve son çıkış hareketleri, sağdan açılan çekmecede.
 */
export function StokDegerCekmecesi({ stok, onKapat, ...ic }: DegerProps) {
  const alt = stok ? [stok.kod, stok.kodlar.urun_grubu].filter(Boolean).join(' · ') : undefined;
  return (
    <RaporCekmecesi acik={!!stok} baslik={stok?.ad ?? ''} altBaslik={alt} onKapat={onKapat} testid="stok-cekmece">
      {/* `key`: başka ürüne geçince önceki ürünün satırları taşınmaz. */}
      {stok && <DegerIcerigi key={stok.id} stok={stok} {...ic} />}
    </RaporCekmecesi>
  );
}

const ALT_BASLIK = 'm-0 text-[12px] font-semibold uppercase tracking-[0.04em] text-ink-500';

function DegerIcerigi({ stok, degerleme, lokasyonKaynagi, fislerKaynagi, bugun }: Omit<DegerProps, 'onKapat' | 'stok'> & { stok: StokDegerSatiri }) {
  const telefon = useDarEkran();
  const maliyet = birimMaliyet(stok, degerleme);

  const lq = useRaporSorgu<HamLokasyonSatiri>(lokasyonKaynagi, { '@STOK': stok.id });
  const lokasyonlar = useMemo(() => (lq.data ?? []).map(lokasyonSatiri).filter((l) => l.elde !== 0), [lq.data]);
  const lokasyonKolonlari = useMemo<RaporKolonu<LokasyonSatiri>[]>(() => [
    { id: 'lokasyon', baslik: 'Lokasyon', tur: 'metin', deger: (l) => l.ad, esnek: true, kart: 'baslik' },
    { id: 'eldeki_miktar', baslik: 'Eldeki Miktar', tur: 'sayi', deger: (l) => l.elde, toplam: true, kart: 'ikincil' },
    { id: 'deger', baslik: 'Değer', tur: 'para', deger: (l) => stokDegeri(l.elde, maliyet), toplam: true, kart: 'olcu' },
  ], [maliyet]);

  // Lokasyon gönderilmez (boş = tümü): ekranda lokasyon filtresi yok.
  const bas = dayjs(bugun).subtract(SON_CIKIS_GUN, 'day').format('YYYY-MM-DD');
  const fq = useRaporSorgu<HamFisSatiri>(fislerKaynagi, { '@STOK': stok.id, '@BASTARIH': bas, '@BITTARIH': bugun });
  const fisler = useMemo(() => fisSatirlari(fq.data ?? []).slice(0, SON_CIKIS_SATIR), [fq.data]);
  const kolonlar = useMemo(() => fisKolonlari(false), []);

  return (
    <div className={GOVDE(telefon)}>
      <div className="flex flex-wrap gap-2.5" data-testid="stok-ozet">
        <Kutu etiket="Son alış">{paraMetni(stok.sonAlis)}</Kutu>
        <Kutu etiket="Ağırlıklı ort.">{paraMetni(stok.agOrt)}</Kutu>
        <Kutu etiket="Son alış tarihi">{tarihMetni(stok.sonAlisTarihi) || '-'}</Kutu>
      </div>

      <h3 className={ALT_BASLIK}>Lokasyonlara dağılım</h3>
      {!lokasyonKaynagi ? (
        <RaporHatasi testid="stok-lokasyon-kaynaksiz" mesaj="Bu firmada lokasyon dağılımı kaynağı tanımlı değil." />
      ) : lq.isError ? (
        <RaporHatasi testid="stok-lokasyon-hata" mesaj="Lokasyon dağılımı yüklenemedi." onTekrar={() => lq.refetch()} />
      ) : (
        <RaporTablosu
          testid="stok-lokasyon-tablosu"
          etiket={`${stok.ad} lokasyon dağılımı`}
          className={telefon ? '' : 'min-h-[120px]'}
          kartStili="cizgi"
          kolonlar={lokasyonKolonlari}
          satirlar={lokasyonlar}
          satirKimligi={(l) => l.id}
          toplamEtiketi="Toplam"
          bosMesaj="Bu ürünün stoğu olan lokasyon yok."
          yukleniyor={lq.isLoading}
          yenileniyor={lq.isFetching && !lq.isLoading}
        />
      )}

      <h3 className={ALT_BASLIK}>Son çıkış hareketleri (son {SON_CIKIS_GUN} gün)</h3>
      {!fislerKaynagi ? (
        <RaporHatasi testid="stok-fis-kaynaksiz" mesaj="Bu firmada çıkış hareketleri kaynağı tanımlı değil." />
      ) : fq.isError ? (
        <RaporHatasi testid="stok-fis-hata" mesaj="Çıkış hareketleri yüklenemedi." onTekrar={() => fq.refetch()} />
      ) : (
        <RaporTablosu
          testid="stok-fis-tablosu"
          etiket={`${stok.ad} son çıkış hareketleri`}
          className={telefon ? 'pb-4' : 'min-h-[120px]'}
          kartStili="cizgi"
          kolonlar={kolonlar}
          satirlar={fisler}
          satirKimligi={(f) => f.sira}
          bosMesaj={`Son ${SON_CIKIS_GUN} günde çıkış hareketi yok.`}
          yukleniyor={fq.isLoading}
          yenileniyor={fq.isFetching && !fq.isLoading}
        />
      )}
    </div>
  );
}

// ── Stok Çıkış ──────────────────────────────────────────────────────────────────

interface CikisProps {
  /** Açık ürün; `undefined` ise çekmece kapalıdır. */
  stok: StokCikisSatiri | undefined;
  kaynak: PanelEkranKaynagi | undefined;
  /** Listenin dönemi ve çıkış türü: çekmece aynı süzgeçle açılır. */
  bas: string;
  bit: string;
  tur: CikisTuru;
  onKapat: () => void;
}

/** S345: ürünün dönemdeki çıkış satırları (listenin dönemi ve çıkış türüyle), sağdan açılan çekmecede. */
export function StokCikisCekmecesi({ stok, kaynak, bas, bit, tur, onKapat }: CikisProps) {
  const alt = stok ? `${stok.kod} · ${tarihMetni(bas)} - ${tarihMetni(bit)} · ${CIKIS_TURU_ADI[tur]}` : undefined;
  return (
    <RaporCekmecesi acik={!!stok} baslik={stok?.ad ?? ''} altBaslik={alt} onKapat={onKapat} testid="cikis-cekmece">
      {stok && <CikisIcerigi key={stok.id} stok={stok} kaynak={kaynak} bas={bas} bit={bit} tur={tur} />}
    </RaporCekmecesi>
  );
}

function CikisIcerigi({ stok, kaynak, bas, bit, tur }: Omit<CikisProps, 'onKapat' | 'stok'> & { stok: StokCikisSatiri }) {
  const telefon = useDarEkran();
  // Çıkış türü ds parametresi değil: satırlar `satis_mi` ile istemcide süzülür (tür değişince sorgu yinelenmez).
  const q = useRaporSorgu<HamFisSatiri>(kaynak, { '@STOK': stok.id, '@BASTARIH': bas, '@BITTARIH': bit });
  const fisler = useMemo(() => fisSatirlari(q.data ?? [], tur), [q.data, tur]);
  const kolonlar = useMemo(() => fisKolonlari(true), []);

  return (
    <div className={GOVDE(telefon)}>
      {!kaynak ? (
        <RaporHatasi testid="cikis-cekmece-kaynaksiz" mesaj="Bu firmada çıkış fişleri kaynağı tanımlı değil." />
      ) : q.isError ? (
        <RaporHatasi testid="cikis-cekmece-hata" mesaj="Çıkış fişleri yüklenemedi." onTekrar={() => q.refetch()} />
      ) : (
        <RaporTablosu
          testid="cikis-fis-tablosu"
          etiket={`${stok.ad} çıkış fişleri`}
          className={telefon ? '' : 'min-h-[140px]'}
          kartStili="cizgi"
          kolonlar={kolonlar}
          satirlar={fisler}
          satirKimligi={(f) => f.sira}
          toplamEtiketi={`Toplam (${fisler.length} satır)`}
          bosMesaj="Bu dönemde çıkış satırı yok."
          yukleniyor={q.isLoading}
          yenileniyor={q.isFetching && !q.isLoading}
        />
      )}
    </div>
  );
}
